import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

const CHECKLIST_COLUMNS = ['ID', 'Сценарий', 'Ожидаемый результат'];
const DEFAULT_SECTION_TITLE = '1. Основные проверки';

const normalizeJob = (raw) => {
  if (!raw || typeof raw !== 'object') {
    return null;
  }
  const status = typeof raw.status === 'string' ? raw.status.toUpperCase() : 'PENDING';
  const completed = Boolean(raw.completed) || status === 'COMPLETE' || status === 'ERROR';
  const error = Boolean(raw.error) || status === 'ERROR';
  const progressValue = Number(raw.progress);
  const progress = Number.isFinite(progressValue)
    ? Math.max(0, Math.min(100, Math.round(progressValue)))
    : 0;

  return {
    ...raw,
    status,
    completed,
    error,
    progress,
    active: !completed && !error,
    message: typeof raw.message === 'string' ? raw.message : '',
  };
};

const hasNonEmptyTestCaseRows = (rows) => {
  if (!Array.isArray(rows)) {
    return false;
  }

  return rows.some((row) => {
    if (!row || typeof row !== 'object') {
      return false;
    }
    return Object.values(row).some((value) => String(value ?? '').trim().length > 0);
  });
};

const extractFileName = (headers, fallback) => {
  const disposition = headers?.['content-disposition'];
  if (typeof disposition !== 'string') {
    return fallback;
  }
  const match = disposition.match(/filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i);
  if (!match) {
    return fallback;
  }
  const encoded = match[1] || match[2];
  try {
    return decodeURIComponent(encoded);
  } catch (error) {
    return encoded || fallback;
  }
};

const downloadBlob = (blobData, filename) => {
  const blob = blobData instanceof Blob
    ? blobData
    : new Blob([blobData ?? new ArrayBuffer(0)]);
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const buildEmptyChecklistRow = (id = '') => ({
  ID: id,
  Сценарий: '',
  'Ожидаемый результат': '',
});

const buildEmptyChecklistSection = (title = DEFAULT_SECTION_TITLE) => ({
  title,
  rows: [],
});

const CHECKLIST_PAGE_SIZE = 25;

const normalizeChecklistSections = (sections) => {
  if (!Array.isArray(sections) || sections.length === 0) {
    return [buildEmptyChecklistSection()];
  }

  const normalized = sections
    .filter((section) => section && typeof section === 'object')
    .map((section, index) => {
      const title = typeof section.title === 'string' && section.title.trim().length > 0
        ? section.title.trim()
        : `${index + 1}. Раздел`;
      const rows = Array.isArray(section.rows)
        ? section.rows
          .filter((row) => row && typeof row === 'object')
          .map((row) => ({
            ID: row.ID == null ? '' : String(row.ID),
            Сценарий: row.Сценарий == null ? '' : String(row.Сценарий),
            'Ожидаемый результат': row['Ожидаемый результат'] == null ? '' : String(row['Ожидаемый результат']),
          }))
        : [];
      return { title, rows };
    });

  return normalized.length > 0 ? normalized : [buildEmptyChecklistSection()];
};

const splitMarkdownCells = (line) => {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  if (trimmed.length === 0) {
    return [];
  }

  const cells = [];
  let current = '';

  for (let index = 0; index < trimmed.length; index += 1) {
    const char = trimmed[index];
    const nextChar = trimmed[index + 1];

    if (char === '\\' && nextChar === '|') {
      current += '|';
      index += 1;
      continue;
    }

    if (char === '|') {
      cells.push(current.trim());
      current = '';
      continue;
    }

    current += char;
  }

  cells.push(current.trim());
  return cells;
};

const isTableSeparator = (line) => {
  const cells = line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .split('|')
    .map((cell) => cell.trim())
    .filter((cell) => cell.length > 0);

  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

const isChecklistHeader = (cells) => {
  if (!Array.isArray(cells) || cells.length < 3) {
    return false;
  }
  const normalized = cells.slice(0, 3).map((cell) => cell.toLowerCase().replace(/\s+/g, ' ').trim());
  return normalized[0] === 'id'
    && normalized[1].includes('сценар')
    && normalized[2].includes('ожидаем');
};

const parseChecklistMarkdown = (checklistText) => {
  if (typeof checklistText !== 'string' || checklistText.trim().length === 0) {
    return [buildEmptyChecklistSection()];
  }

  const lines = checklistText.replace(/\r\n/g, '\n').split('\n');
  const sections = [];

  const ensureSection = () => {
    if (sections.length === 0) {
      sections.push(buildEmptyChecklistSection());
    }
    return sections[sections.length - 1];
  };

  lines.forEach((rawLine) => {
    const line = rawLine.trim();
    if (line.length === 0) {
      return;
    }

    if (/^##\s+/.test(line)) {
      const title = line.replace(/^##\s+/, '').trim();
      if (title.length > 0 && !/^итоги покрытия$/i.test(title)) {
        sections.push(buildEmptyChecklistSection(title));
      }
      return;
    }

    if (!line.startsWith('|')) {
      const legacyMatch = line.match(/^-\s*\[[ xX]?\]\s*([^:]+):\s*(.+?)(?:\.?\s*Ожидаемый результат:\s*(.+))?$/i);
      if (legacyMatch) {
        const section = ensureSection();
        section.rows.push({
          ID: legacyMatch[1]?.trim() || '',
          Сценарий: legacyMatch[2]?.trim() || '',
          'Ожидаемый результат': legacyMatch[3]?.trim() || '',
        });
      }
      return;
    }

    if (isTableSeparator(line)) {
      return;
    }

    const cells = splitMarkdownCells(line);
    if (isChecklistHeader(cells)) {
      return;
    }

    if (cells.length < 3) {
      return;
    }

    const section = ensureSection();
    section.rows.push({
      ID: cells[0] || '',
      Сценарий: cells[1] || '',
      'Ожидаемый результат': cells[2] || '',
    });
  });

  return normalizeChecklistSections(sections);
};

const escapeMarkdownCell = (value) => String(value ?? '')
  .replace(/\r?\n/g, ' ')
  .replace(/\|/g, '\\|')
  .trim();

const serializeChecklistMarkdown = (sections) => {
  const normalized = normalizeChecklistSections(sections);
  const lines = ['# Чек-лист тест-кейсов'];

  normalized.forEach((section, index) => {
    const title = section.title.trim().length > 0 ? section.title.trim() : `${index + 1}. Раздел`;
    lines.push(`## ${title}`);
    lines.push('| ID | Сценарий | Ожидаемый результат |');
    lines.push('|----|----------|----------------------|');

    section.rows.forEach((row) => {
      lines.push(`| ${escapeMarkdownCell(row.ID)} | ${escapeMarkdownCell(row.Сценарий)} | ${escapeMarkdownCell(row['Ожидаемый результат'])} |`);
    });
  });

  return lines.join('\n');
};

const nextCaseId = (sections) => {
  let max = 0;
  sections.forEach((section) => {
    section.rows.forEach((row) => {
      const match = String(row.ID || '').trim().match(/^TC-(\d+)$/i);
      if (!match) {
        return;
      }
      const value = Number.parseInt(match[1], 10);
      if (Number.isFinite(value) && value > max) {
        max = value;
      }
    });
  });

  return `TC-${String(max + 1).padStart(2, '0')}`;
};

export default function StepFourChecklistPanel({
  projectId,
  apiClient,
  refreshSignal = 0,
  disabled = false,
  disabledReason = null,
}) {
  const [checklistJob, setChecklistJob] = useState(null);
  const [checklistArtifact, setChecklistArtifact] = useState(null);
  const [checklistSections, setChecklistSections] = useState([buildEmptyChecklistSection()]);
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistError, setChecklistError] = useState(null);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [checklistDownloading, setChecklistDownloading] = useState(false);
  const [sourceTestCasesReady, setSourceTestCasesReady] = useState(false);
  const [checklistPages, setChecklistPages] = useState({});

  const hasChecklistArtifact = Boolean(checklistArtifact?.id);

  const applyChecklistArtifact = useCallback((artifact) => {
    setChecklistArtifact(artifact || null);
    setChecklistSections(parseChecklistMarkdown(artifact?.checklistText));
  }, []);

  const loadChecklistArtifactByJob = useCallback(async (jobId) => {
    if (!jobId) {
      return;
    }
    const response = await apiClient.get(`/api/projects/${projectId}/story-qa/checklists/jobs/${jobId}/artifact`);
    applyChecklistArtifact(response?.data ?? null);
  }, [apiClient, applyChecklistArtifact, projectId]);

  const refreshSourceTestCasesState = useCallback(async () => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/story-qa/test-cases/artifacts/latest`);
      const ready = hasNonEmptyTestCaseRows(response?.data?.rows);
      setSourceTestCasesReady(ready);
      return ready;
    } catch (error) {
      setSourceTestCasesReady(false);
      return false;
    }
  }, [apiClient, projectId]);

  const pollChecklistJob = useCallback(async (jobId) => {
    try {
      const response = await apiClient.get(`/api/projects/${projectId}/story-qa/checklists/jobs/${jobId}`);
      const normalized = normalizeJob(response?.data);
      if (!normalized) {
        return;
      }
      setChecklistJob(normalized);
      if (normalized.completed && !normalized.error) {
        await loadChecklistArtifactByJob(normalized.jobId);
      }
    } catch (error) {
      setChecklistError(error?.response?.data?.error || error.message || 'Не удалось получить статус генерации чек-листа.');
    }
  }, [apiClient, loadChecklistArtifactByJob, projectId]);

  const loadInitialState = useCallback(async () => {
    setChecklistLoading(true);
    setChecklistError(null);

    try {
      const [artifactRes, activeRes, sourceRes] = await Promise.allSettled([
        apiClient.get(`/api/projects/${projectId}/story-qa/checklists/artifacts/latest`),
        apiClient.get(`/api/projects/${projectId}/story-qa/checklists/jobs/active`),
        apiClient.get(`/api/projects/${projectId}/story-qa/test-cases/artifacts/latest`),
      ]);

      if (artifactRes.status === 'fulfilled') {
        applyChecklistArtifact(artifactRes.value?.data ?? null);
      }

      if (activeRes.status === 'fulfilled') {
        setChecklistJob(normalizeJob(activeRes.value?.data));
      }

      if (sourceRes.status === 'fulfilled') {
        setSourceTestCasesReady(hasNonEmptyTestCaseRows(sourceRes.value?.data?.rows));
      } else {
        setSourceTestCasesReady(false);
      }
    } catch (error) {
      setChecklistError(error?.response?.data?.error || error.message || 'Не удалось загрузить данные шага 4.');
    } finally {
      setChecklistLoading(false);
    }
  }, [apiClient, applyChecklistArtifact, projectId]);

  useEffect(() => {
    setChecklistJob(null);
    setChecklistArtifact(null);
    setChecklistSections([buildEmptyChecklistSection()]);
    setSourceTestCasesReady(false);
    loadInitialState();
  }, [loadInitialState, projectId]);

  useEffect(() => {
    if (refreshSignal > 0) loadInitialState();
  }, [loadInitialState, refreshSignal]);

  useEffect(() => {
    if (!checklistJob?.active || !checklistJob?.jobId) {
      return undefined;
    }

    const timer = setInterval(() => {
      pollChecklistJob(checklistJob.jobId);
    }, 2000);

    return () => clearInterval(timer);
  }, [pollChecklistJob, checklistJob?.active, checklistJob?.jobId]);

  const handleGenerateChecklist = useCallback(async () => {
    if (disabled || checklistJob?.active) {
      return;
    }

    setChecklistError(null);
    const hasSourceTestCases = await refreshSourceTestCasesState();
    if (!hasSourceTestCases) {
      setChecklistError('Сначала сгенерируйте тест-кейсы на шаге 3.');
      return;
    }

    try {
      const response = await apiClient.post(`/api/projects/${projectId}/story-qa/checklists/generate`);
      const normalized = normalizeJob(response?.data);
      setChecklistJob(normalized);
      if (normalized?.completed && !normalized.error) {
        await loadChecklistArtifactByJob(normalized.jobId);
      }
    } catch (error) {
      setChecklistError(error?.response?.data?.error || error.message || 'Не удалось запустить генерацию чек-листа.');
    }
  }, [apiClient, checklistJob?.active, disabled, loadChecklistArtifactByJob, projectId, refreshSourceTestCasesState]);

  const handleSaveChecklist = useCallback(async () => {
    if (!hasChecklistArtifact || checklistSaving) {
      return;
    }

    setChecklistSaving(true);
    setChecklistError(null);
    try {
      const response = await apiClient.put(
        `/api/projects/${projectId}/story-qa/checklists/artifacts/${checklistArtifact.id}`,
        { checklistText: serializeChecklistMarkdown(checklistSections) }
      );
      applyChecklistArtifact(response?.data ?? null);
    } catch (error) {
      setChecklistError(error?.response?.data?.error || error.message || 'Не удалось сохранить чек-лист.');
    } finally {
      setChecklistSaving(false);
    }
  }, [apiClient, applyChecklistArtifact, checklistArtifact?.id, checklistSaving, checklistSections, hasChecklistArtifact, projectId]);

  const handleAddSection = useCallback(() => {
    setChecklistSections((prev) => [
      ...prev,
      buildEmptyChecklistSection(`${prev.length + 1}. Новый раздел`),
    ]);
  }, []);

  const handleRemoveSection = useCallback((sectionIndex) => {
    setChecklistSections((prev) => {
      const next = prev.filter((_, index) => index !== sectionIndex);
      return next.length > 0 ? next : [buildEmptyChecklistSection()];
    });
  }, []);

  const handleSectionTitleChange = useCallback((sectionIndex, value) => {
    setChecklistSections((prev) => prev.map((section, index) => (
      index === sectionIndex ? { ...section, title: value } : section
    )));
  }, []);

  const handleAddChecklistRow = useCallback((sectionIndex) => {
    setChecklistSections((prev) => {
      const generatedId = nextCaseId(prev);
      return prev.map((section, index) => (
        index === sectionIndex
          ? { ...section, rows: [...section.rows, buildEmptyChecklistRow(generatedId)] }
          : section
      ));
    });
  }, []);

  const handleRemoveChecklistRow = useCallback((sectionIndex, rowIndex) => {
    setChecklistSections((prev) => prev.map((section, index) => {
      if (index !== sectionIndex) {
        return section;
      }
      return {
        ...section,
        rows: section.rows.filter((_, currentIndex) => currentIndex !== rowIndex),
      };
    }));
  }, []);

  const handleChecklistCellChange = useCallback((sectionIndex, rowIndex, column, value) => {
    setChecklistSections((prev) => prev.map((section, index) => {
      if (index !== sectionIndex) {
        return section;
      }
      return {
        ...section,
        rows: section.rows.map((row, currentRowIndex) => {
          if (currentRowIndex !== rowIndex) {
            return row;
          }
          return { ...row, [column]: value };
        }),
      };
    }));
  }, []);

  const handleDownloadChecklist = useCallback(async () => {
    if (!hasChecklistArtifact || checklistDownloading) {
      return;
    }

    setChecklistDownloading(true);
    setChecklistError(null);
    try {
      const response = await apiClient.get(
        `/api/projects/${projectId}/story-qa/checklists/artifacts/${checklistArtifact.id}/download`,
        { responseType: 'blob' }
      );
      const filename = extractFileName(response?.headers, 'qa-checklist.md');
      downloadBlob(response?.data, filename);
    } catch (error) {
      setChecklistError(error?.response?.data?.error || error.message || 'Не удалось скачать чек-лист.');
    } finally {
      setChecklistDownloading(false);
    }
  }, [apiClient, checklistArtifact?.id, checklistDownloading, hasChecklistArtifact, projectId]);

  const checklistStatus = useMemo(() => {
    if (checklistJob?.active) {
      return { label: 'В процессе', color: 'info' };
    }
    if (checklistJob?.error) {
      return { label: 'Ошибка', color: 'error' };
    }
    if (hasChecklistArtifact) {
      return { label: 'Готово', color: 'success' };
    }
    return { label: 'Не запущено', color: 'default' };
  }, [checklistJob?.active, checklistJob?.error, hasChecklistArtifact]);

  return (
    <Paper className="surface-glow" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
        >
          <Typography variant="h6">Шаг 4. QA чек-лист</Typography>
          <Chip size="small" label={`QA чек-лист: ${checklistStatus.label}`} color={checklistStatus.color} variant="outlined" />
        </Stack>

        {disabled && (
          <Alert severity="info">
            {disabledReason || 'LLM-операции временно недоступны.'}
          </Alert>
        )}

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="subtitle1">Checklist для QA (из тест-кейсов шага 3)</Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleGenerateChecklist}
                disabled={disabled || checklistJob?.active || checklistLoading}
              >
                {checklistJob?.active ? 'Генерация…' : 'Сгенерировать'}
              </Button>
            </Stack>

            {checklistLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">Загрузка чек-листа…</Typography>
              </Stack>
            ) : null}

            {!checklistLoading && !sourceTestCasesReady && (
              <Alert severity="info">
                Для генерации шага 4 нужны тест-кейсы из шага 3.
              </Alert>
            )}

            {checklistError && <Alert severity="error">{checklistError}</Alert>}

            {checklistJob?.active && (
              <Stack spacing={0.5}>
                <Typography variant="body2" color="text.secondary">
                  {checklistJob.message || 'Генерация чек-листа в процессе…'}
                </Typography>
                <LinearProgress variant="determinate" value={checklistJob.progress || 0} />
              </Stack>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Редактирование чек-листа в таблице (построен по тест-кейсам шага 3).
              </Typography>
              <Button size="small" onClick={handleAddSection}>Добавить раздел</Button>
            </Stack>

            <Stack spacing={1.5}>
              {checklistSections.map((section, sectionIndex) => (
                <Paper key={`checklist-section-${sectionIndex}`} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack spacing={1.25}>
                    <Stack
                      direction={{ xs: 'column', md: 'row' }}
                      spacing={1}
                      alignItems={{ xs: 'stretch', md: 'center' }}
                      justifyContent="space-between"
                    >
                      <TextField
                        label={`Раздел ${sectionIndex + 1}`}
                        value={section.title}
                        onChange={(event) => handleSectionTitleChange(sectionIndex, event.target.value)}
                        size="small"
                        fullWidth
                      />
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => handleAddChecklistRow(sectionIndex)}>Добавить строку</Button>
                        <Tooltip title="Удалить раздел">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRemoveSection(sectionIndex)}
                              disabled={checklistSections.length <= 1}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </Stack>

                    <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
                      <Table size="small" sx={{ minWidth: 860 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ width: 48 }}>#</TableCell>
                            {CHECKLIST_COLUMNS.map((column) => (
                              <TableCell key={`${sectionIndex}-${column}`}>{column}</TableCell>
                            ))}
                            <TableCell align="right" sx={{ width: 56 }}>Действия</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {section.rows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={CHECKLIST_COLUMNS.length + 2}>
                                <Typography variant="body2" color="text.secondary">
                                  Пока нет строк в разделе.
                                </Typography>
                              </TableCell>
                            </TableRow>
                          ) : section.rows
                            .slice((checklistPages[sectionIndex] || 0) * CHECKLIST_PAGE_SIZE,
                              ((checklistPages[sectionIndex] || 0) + 1) * CHECKLIST_PAGE_SIZE)
                            .map((row, visibleRowIndex) => {
                              const rowIndex = (checklistPages[sectionIndex] || 0) * CHECKLIST_PAGE_SIZE + visibleRowIndex;
                              return (
                            <TableRow key={`checklist-row-${sectionIndex}-${rowIndex}`}>
                              <TableCell>{rowIndex + 1}</TableCell>
                              {CHECKLIST_COLUMNS.map((column) => (
                                <TableCell key={`checklist-cell-${sectionIndex}-${rowIndex}-${column}`} sx={{ minWidth: column === 'ID' ? 120 : 260 }}>
                                  <TextField
                                    value={row[column] || ''}
                                    onChange={(event) => handleChecklistCellChange(sectionIndex, rowIndex, column, event.target.value)}
                                    size="small"
                                    multiline={column !== 'ID'}
                                    minRows={column === 'ID' ? 1 : 2}
                                    fullWidth
                                  />
                                </TableCell>
                              ))}
                              <TableCell align="right">
                                <Tooltip title="Удалить строку">
                                  <span>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveChecklistRow(sectionIndex, rowIndex)}
                                    >
                                      <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                              );
                            })}
                        </TableBody>
                      </Table>
                      {section.rows.length > CHECKLIST_PAGE_SIZE && (
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ p: 1 }}>
                          <Button size="small" disabled={!checklistPages[sectionIndex]} onClick={() => setChecklistPages((prev) => ({ ...prev, [sectionIndex]: Math.max(0, (prev[sectionIndex] || 0) - 1) }))}>Назад</Button>
                          <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                            Страница {(checklistPages[sectionIndex] || 0) + 1} из {Math.ceil(section.rows.length / CHECKLIST_PAGE_SIZE)}
                          </Typography>
                          <Button size="small" disabled={(checklistPages[sectionIndex] || 0) >= Math.ceil(section.rows.length / CHECKLIST_PAGE_SIZE) - 1} onClick={() => setChecklistPages((prev) => ({ ...prev, [sectionIndex]: (prev[sectionIndex] || 0) + 1 }))}>Вперёд</Button>
                        </Stack>
                      )}
                    </Paper>
                  </Stack>
                </Paper>
              ))}
            </Stack>

            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                onClick={handleSaveChecklist}
                disabled={!hasChecklistArtifact || checklistSaving}
              >
                {checklistSaving ? 'Сохранение…' : 'Сохранить'}
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleDownloadChecklist}
                disabled={!hasChecklistArtifact || checklistDownloading}
              >
                {checklistDownloading ? 'Скачивание…' : 'Скачать MD'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Stack>
    </Paper>
  );
}
